import { UseQueryOptions, useMutation, useQuery } from "@tanstack/react-query";

import type { DTO } from "@equidefi/shared/types";
import InquiryClient from "../clients/InquiryClient";
import { ApiClientClass, useApi } from "./useApi";

export const useInvestmentInquiries = (
  investmentId: string,
  options: UseQueryOptions<{ data: DTO.TInquiry[] }> = {},
) => {
  const inquiryApi = useApi(InquiryClient as unknown as ApiClientClass<InquiryClient>);

  return useQuery<{ data: DTO.TInquiry[] }>(
    ["investment", investmentId, "inquiries"],
    () => inquiryApi.getInvestmentInquiries(investmentId),
    {
      enabled: !!investmentId,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      ...options,
    },
  );
};

export const useResumeInquiryUrl = (inquiryId: string, options = {}) => {
  const api = useApi(InquiryClient as unknown as ApiClientClass<InquiryClient>);

  return useMutation(
    (data: { userId: string; inquiryType: string; citizenship: string }) => api.resume(inquiryId, data),
    options,
  );
};

export const useGetInquiry = (
  inquiryId: string,

  options = {},
) => {
  const inquiryApi = useApi(InquiryClient as unknown as ApiClientClass<InquiryClient>);

  return useQuery([inquiryId, "inquiry"], () => inquiryApi.find(inquiryId), {
    enabled: !!inquiryId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    ...options,
  });
};

export const useGetFullInquiry = (
  inquiryId: string | undefined,
  params: { investmentId: string; entityType: string; citizenship: string },
  options: UseQueryOptions<DTO.FullInquiry> = {},
) => {
  const inquiryApi = useApi(InquiryClient as unknown as ApiClientClass<InquiryClient>);

  return useQuery<DTO.FullInquiry>(
    ["inquiry", inquiryId, "full", params],
    () => inquiryApi.getFull(inquiryId as string, params),
    {
      enabled: !!inquiryId && !!params,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      ...options,
    },
  );
};

export const useSendInquiryEmail = (inquiryId: string, investmentId: string, options = {}) => {
  const api = useApi(InquiryClient as unknown as ApiClientClass<InquiryClient>);

  return useMutation((data: Record<string, string> = {}) => api.emailInvestor(inquiryId, investmentId, data), options);
};

export const useStartInquiry = (investmentId: string, options = {}) => {
  const api = useApi(InquiryClient as unknown as ApiClientClass<InquiryClient>);

  return useMutation(
    (data: {
      userId: string;
      entityType: string;
      citizenship: string;
      inquiryType: string;
      options?: {
        overwrites?: Record<string, any>;
        skipEmbedded?: Boolean;
      };
    }) => api.start(investmentId, data.userId, data.entityType, data.citizenship, data.inquiryType, data.options),
    options,
  );
};

export const useApproveInquiry = (investmentId: string, inquiryId: string, options = {}) => {
  const api = useApi(InquiryClient as unknown as ApiClientClass<InquiryClient>);

  return useMutation(
    (data: {
      userId: string;
      entityType: string;
      citizenship: string;
      inquiryType: string;
      comment: string;
    }) => api.approve(investmentId, inquiryId, data),
    options,
  );
};

export const useDeclineInquiry = (investmentId: string, inquiryId?: string, options = {}) => {
  const api = useApi(InquiryClient as unknown as ApiClientClass<InquiryClient>);

  return useMutation(
    (data: {
      userId: string;
      entityType: string;
      citizenship: string;
      inquiryType: string;
      comment: string;
    }) => api.decline(investmentId, inquiryId, data),
    options,
  );
};
